import { ReactNode } from 'react';

import {
   AppointmentCreateCommand,
   AppointmentCreationResult,
   AppointmentEncounterMode,
   AppointmentTimeSlotOccupiedFault,
} from '@dreryk/egabinet-appointment-frontend-api';
import { ReceptionServiceId } from '@dreryk/egabinet-private-medical-service-published-language-api';
import { TimeSlot } from '@dreryk/time-slot-common-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import {
   ActionEventInterceptor,
   dispatchSnackbarNegativeEvent,
   dispatchSnackbarPositiveEvent,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { AppointmentAdvancePaymentShowModalEventInterceptor } from '~libs.desktop.shared.appointment-advance-payment';

import { appointmentsTranslationKeys } from '../../../../translations';
import {
   parseToDateWithMidnightAdjustment,
   resolveRecurrenceParameters,
} from '../../shared/form/AppointmentForm.utils';
import { AppointmentCreateFormBean, AppointmentCreateFormController } from '../shared/AppointmentCreateForm';

import { getFirstAppointmentId } from './AppointmentCreateActionEventInterceptor.utils';

type AppointmentCreateActionEventInterceptorProps = Readonly<
   Pick<AppointmentCreateFormController, 'handleSubmit'> & {
      children: ReactNode;
      onAppointmentTimeSlotOccupiedFault: VoidFunction;
   }
>;

export const AppointmentCreateActionEventInterceptor = (
   props: AppointmentCreateActionEventInterceptorProps
): JSX.Element => {
   const { executeAsync: createAppointment, data: appointmentCreationResult } = useCqrsCommand<
      AppointmentCreationResult,
      AppointmentCreateFormBean,
      AppointmentTimeSlotOccupiedFault
   >(
      (parameters) => {
         const command = new AppointmentCreateCommand();
         command.location = parameters.healthcareProviderLocation!.locationIdentifier;
         command.timeSlot = TimeSlot.of(
            parseToDateWithMidnightAdjustment('START', parameters.date, parameters.startTime),
            parseToDateWithMidnightAdjustment('END', parameters.date, parameters.endTime)
         );
         command.patientId = parameters.patient!.id!;
         command.practitionerId = parameters.practitioner.id;
         command.note = Choice.withUseDefaultIfNull(parameters.note);

         if (parameters.videoConsultation) {
            command.encounterMode = Choice.of<AppointmentEncounterMode>('VIDEO_CONSULTATION');
         }

         if (parameters.recurrence) {
            command.recurrenceParameters = Choice.withUseDefaultIfNull(resolveRecurrenceParameters(parameters));
         }

         if (parameters.receptionService?.value) {
            command.receptionServiceIds = parameters.receptionService.value.map(
               ({ receptionService: { receptionServiceId } }) => ReceptionServiceId.of(receptionServiceId)
            );
         }
         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(appointmentsTranslationKeys.successfullyCreatedNewAppointment);
         },
         onFault: {
            'egabinet-appointment-frontend-api:AppointmentTimeSlotOccupiedFault': () => {
               dispatchSnackbarNegativeEvent(appointmentsTranslationKeys.timeSlotTaken);
               props.onAppointmentTimeSlotOccupiedFault();
            },
         },
      }
   );

   return (
      <ActionEventInterceptor action={props.handleSubmit(createAppointment)}>
         <AppointmentAdvancePaymentShowModalEventInterceptor
            appointmentId={getFirstAppointmentId(appointmentCreationResult)}
         >
            {props.children}
         </AppointmentAdvancePaymentShowModalEventInterceptor>
      </ActionEventInterceptor>
   );
};
